import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from "react";
import { themeContext } from "../../context/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { playSound, focusOnMouseMove } from "../../utilities/utilities";
import { useGamepad } from "../../hooks/useGamepad";
import { useAuth } from "../../context/auth";
import styles from "./SkinPreviewModal.module.css";

// Preview image mappings for each theme (keyed by name, not ID)
export const THEME_PREVIEWS = {
  "Blaze": {
    name: "Blaze",
    color: "red",
    images: [
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770454130/blaze/one_ckezk9.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770454131/blaze/two_opnlzl.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770454131/blaze/three_hmgclx.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770454130/blaze/four_uq7mt3.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770454130/blaze/five_rpq1bs.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1772478121/blaze/seven_ccg3cf.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770454130/blaze/six_pcioil.png"
    ]
  },
  "Frost": {
    name: "Frost",
    color: "cyan",
    images: [
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770453372/frost/one_wx1tna.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770453372/frost/two_ang66v.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770546203/frost/three_fe6lho.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770453372/frost/four_ibg6gu.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770453372/frost/five_kotnmr.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1772478121/blaze/seven_ccg3cf.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770453372/frost/six_ce2tkt.png"
    ]
  },
  "Spectrum": {
    name: "Spectrum",
    color: "spectrum",
    images: [
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770456657/spectrum/one_fjy75h.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770456657/spectrum/two_n24kyi.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770456657/spectrum/three_cwcznx.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770456655/spectrum/four_n0lz8j.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770456656/spectrum/five_grkfsx.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1772478121/blaze/seven_ccg3cf.png",
      "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1770456658/spectrum/six_lrnyxw.png"
    ]
  },
};


const SkinPreviewModal = ({ themeData, onClose, onPurchase, onSelect, game, isOwned = false, isTrial = false }) => {
  const { theme } = useContext(themeContext);
  const themeNumber = theme.replace("theme-", "");
  const { user, signIn } = useAuth();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null); // 'left' or 'right'
  const [gamepadEnabled, setGamepadEnabled] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const modalRef = useRef(null);

  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  // Enable gamepad with delay to prevent immediate button press from opening/closing modal
  useEffect(() => {
    const timer = setTimeout(() => {
      setGamepadEnabled(true);
    }, 200); // Small delay to prevent button that opened modal from closing it
    return () => clearTimeout(timer);
  }, []);

  // Get preview data for this theme
  const previewData = THEME_PREVIEWS[themeData?.name];
  const images = useMemo(() => previewData?.images || [], [previewData?.images]);
  const currentImage = images[currentImageIndex];
  const hasImages = images.length > 0;

  // Preload all images
  useEffect(() => {
    if (hasImages) {
      images.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [images, hasImages]);

  // Handle image navigation
  const goToNext = useCallback(() => {
    playSound(game, "navigation");
    setSlideDirection('left');
    setImageLoading(true);
    setTimeout(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }, 50);
  }, [images.length, game]);

  const goToPrevious = useCallback(() => {
    playSound(game, "navigation");
    setSlideDirection('right');
    setImageLoading(true);
    setTimeout(() => {
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
    }, 50);
  }, [images.length, game]);

  const goToImage = useCallback((index) => {
    if (index !== currentImageIndex && index >= 0 && index < images.length) {
      playSound(game, "navigation");
      // Determine direction based on index change
      setSlideDirection(index > currentImageIndex ? 'left' : 'right');
      setImageLoading(true);
      setTimeout(() => {
        setCurrentImageIndex(index);
      }, 50);
    }
  }, [currentImageIndex, images.length, game]);


  // Handle close
  const handleClose = useCallback(() => {
    playSound(game, "click");
    onClose();
  }, [game, onClose]);

  // Handle purchase
  const handlePurchase = useCallback(() => {
    playSound(game, "click");
    onPurchase();
  }, [game, onPurchase]);

  // Handle select (for owned themes)
  const handleSelect = useCallback(async () => {
    playSound(game, "click");
    if (onSelect) {
      setIsSelecting(true);
      try {
        await onSelect();
      } finally {
        setIsSelecting(false);
      }
    }
  }, [game, onSelect]);

  // Handle gamepad navigation
  const handleGamepadNavigation = useCallback((direction) => {
    if (!modalRef.current) return;

    const elements = Array.from(modalRef.current.querySelectorAll('[data-focusable="true"]'));
    if (elements.length === 0) return;

    focusableElements.elements = elements;

    if (focusableElements.index === undefined || focusableElements.index >= elements.length) {
      focusableElements.index = 0;
    }

    if (direction === 'left' || direction === 'up') {
      if (focusableElements.index <= 0) {
        focusableElements.index = elements.length - 1;
      } else {
        focusableElements.index--;
      }
    } else if (direction === 'right' || direction === 'down') {
      if (focusableElements.index >= elements.length - 1) {
        focusableElements.index = 0;
      } else {
        focusableElements.index++;
      }
    }

    if (elements[focusableElements.index]) {
      playSound(game, "navigation");
      elements[focusableElements.index].focus();
    }
  }, [focusableElements, game]);

  // Handle gamepad button presses
  const handleGamepadButtonPress = useCallback((button) => {
    if (button === 'X' || button === 'A') {
      if (!modalRef.current) return;

      const elements = Array.from(modalRef.current.querySelectorAll('[data-focusable="true"]'));
      if (focusableElements.index < elements.length && elements[focusableElements.index]) {
        playSound(game, "click");
        elements[focusableElements.index].click();
      }
    } else if (button === 'B' || button === 'Start') {
      playSound(game, "click");
      handleClose();
    }
  }, [focusableElements, handleClose, game]);

  // Use useGamepad hook
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: gamepadEnabled,
    gamepadIndex: null
  });

  useEffect(() => {
    if (modalRef.current) {
      const elements = modalRef.current.querySelectorAll('[data-focusable="true"]');
      const elementsArray = Array.from(elements);
      focusableElements.elements = elementsArray;

      if (focusableElements.index >= elementsArray.length) {
        focusableElements.index = elementsArray.length - 1;
      }

      if (elementsArray.length > 0 && elementsArray[focusableElements.index]) {
        elementsArray[focusableElements.index].focus();
      }
    }
  }, [currentImageIndex, isOwned, focusableElements]);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();

      const elements = modalRef.current.querySelectorAll('[data-focusable="true"]');
      focusableElements.elements = Array.from(elements);
      focusableElements.index = 0;

      if (elements.length > 0) {
        elements[0].focus();
      }
    }
  }, []);

  // Handle image load
  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
    // Reset slide direction after animation completes
    setTimeout(() => {
      setSlideDirection(null);
    }, 300);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
    setTimeout(() => {
      setSlideDirection(null);
    }, 300);
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        className={`${styles.modal} ${styles["modal-theme-" + themeNumber]}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(event) => {
          const modal = event.currentTarget;
          const elements = Array.from(modal.querySelectorAll('[data-focusable="true"]'));
          if (elements.length === 0) return;

          focusableElements.elements = elements;

          if (focusableElements.index === undefined || focusableElements.index >= elements.length) {
            focusableElements.index = 0;
          }

          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (focusableElements.index <= 0) {
              focusableElements.index = elements.length - 1;
            } else {
              focusableElements.index--;
            }
            if (elements[focusableElements.index]) {
              playSound(game, "navigation");
              elements[focusableElements.index].focus();
            }
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            if (focusableElements.index >= elements.length - 1) {
              focusableElements.index = 0;
            } else {
              focusableElements.index++;
            }
            if (elements[focusableElements.index]) {
              playSound(game, "navigation");
              elements[focusableElements.index].focus();
            }
          } else if (event.key === 'Enter') {
            event.preventDefault();
            if (elements[focusableElements.index]) {
              playSound(game, "click");
              elements[focusableElements.index].click();
            }
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            playSound(game, "click");
            handleClose();
          }
        }}
        onMouseMove={(event) => {
          const modal = event.currentTarget;
          const elements = Array.from(modal.querySelectorAll('[data-focusable="true"]'));
          if (elements.length > 0) {
            focusableElements.elements = elements;
            focusOnMouseMove(event, focusableElements, game, playSound);
          }
        }}
        tabIndex={0}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{themeData.name}</h2>
            {hasImages && (
              <span className={styles.imageCounter}>
                {currentImageIndex + 1} / {images.length}
              </span>
            )}
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.iconButton}
              onClick={handleClose}
              aria-label="Close preview"
              title="Close (Esc)"
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Thumbnail Strip - only show if images exist */}
        {hasImages && (
          <div className={styles.thumbnailContainer}>
            <div className={styles.thumbnailStrip}>
              {images.map((img, index) => (
                <button
                  key={index}
                  className={`${styles.thumbnail} ${
                    index === currentImageIndex ? styles.thumbnailActive : ""
                  }`}
                  onClick={() => goToImage(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img src={img} alt={`Thumbnail ${index + 1}`} />
                  {index === currentImageIndex && (
                    <div className={styles.thumbnailOverlay}></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Image Display */}
        <div className={styles.imageContainer}>
          {/* Image or No Assets Message */}
          <div className={styles.imageWrapper}>
            {!hasImages ? (
              <div className={styles.noAssets}>
                <p>NO ASSETS</p>
              </div>
            ) : (
              <>
                {imageLoading && (
                  <div className={styles.imageLoader}>
                    <div className={styles.spinner}></div>
                    <p>Loading...</p>
                  </div>
                )}
                {imageError ? (
                  <div className={styles.imageError}>
                    <p>Failed to load image</p>
                  </div>
                ) : (
                  <img
                    src={currentImage}
                    alt={`${themeData.name} preview ${currentImageIndex + 1}`}
                    className={`${styles.mainImage} ${imageLoading ? styles.hidden : ""} ${
                      slideDirection === 'left' ? styles.slideInFromRight :
                      slideDirection === 'right' ? styles.slideInFromLeft : ''
                    }`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Bundle Info - show for all paid app skins not purchased */}
        {!themeData.isOwned && themeData.isPaid && (
          <div className={styles.bundleInfo}>
            <p className={styles.bundleText}>
              Includes {themeData.name} board + {themeData.name} app skin
            </p>
          </div>
        )}

        {/* Footer with Navigation Buttons */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {user && isOwned && (
              <button
                className={styles.purchaseButton}
                onClick={handleSelect}
                disabled={isSelecting}
                data-focusable="true"
              >
                <span>Select</span>
              </button>
            )}

            {!themeData.isOwned && themeData.isPaid && (
              <button
                className={styles.purchaseButton}
                onClick={handlePurchase}
                data-focusable="true"
              >
                <span>Buy — ${themeData.price || 0}</span>
              </button>
            )}

            {!user && themeData.isTrialAccessible && (
              <button
                className={styles.tryButton}
                onClick={() => { playSound(game, "click"); signIn(); }}
                data-focusable="true"
              >
                <span>Try for Free</span>
              </button>
            )}
          </div>

          {hasImages && (
            <div className={styles.footerRight}>
              <button
                className={`${styles.navButton} ${styles.navButtonLeft}`}
                onClick={goToPrevious}
                aria-label="Previous image"
                data-focusable="true"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                className={`${styles.navButton} ${styles.navButtonRight}`}
                onClick={goToNext}
                aria-label="Next image"
                data-focusable="true"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SkinPreviewModal;
